import { useEffect, useState, ReactNode } from 'react';
import { User, AuthState } from '@/types';
import { authService } from '@/api/auth';
import { AUTH_CONSTANTS } from '@/constants/auth';
import { TokenManager } from '@/utils/tokenManager';
import { AuthContext } from './AuthContextObject';
import { useAuthStore } from '@/stores/useAuthStore';

interface LocalAuthContextType extends AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentials: { username: string; password: string }) => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

// Context moved to separate file to satisfy react-refresh

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // NUR als authentifiziert betrachten wenn User UND Token vorhanden UND nicht am Ausloggen
  const isLoggingOut = useAuthStore((state) => state.isLoggingOut);
  const isAuthenticated: boolean = !!user && authService.isAuthenticated() && !isLoggingOut;

  // Load user on mount
  useEffect(() => {
    const loadUser = async () => {
      console.log('=== AuthContext: loadUser called ===');
      
      // Skip wenn bereits ausgeloggt wird
      if (isLoggingOut) {
        setUser(null);
        setIsLoading(false);
        return;
      }
      
      // SCHRITT 1: CSRF-Token holen (IMMER, auch ohne Auth!)
      try {
        console.log('🔒 Fetching CSRF token...');
        await fetch('/api/csrf/', { credentials: 'include' });
        console.log('✅ CSRF token fetched');
      } catch (error) {
        console.warn('⚠️ Failed to fetch CSRF token:', error);
      }
      
      // Erst prüfen ob Token existiert
      if (!authService.isAuthenticated()) {
        console.log('No tokens found, setting user to null');
        setUser(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        
        console.log('Tokens found, validating...');
        // Token validieren durch API-Call
        const isValid = await authService.validateToken();
        if (!isValid) {
          console.log('Token validation failed, setting user to null');
          setUser(null);
          return;
        }
        
        console.log('Token valid, fetching user data...');
        const userData = await authService.getCurrentUser();
        setUser(userData);
        console.log('User data loaded:', userData);
      } catch (err: unknown) {
        const message = (err as { message?: string })?.message ?? 'Failed to load user';
        console.log('Token invalid, setting user to null:', message);
        setError(message);
        setUser(null);
        // Tokens manuell löschen
        TokenManager.removeToken(AUTH_CONSTANTS.TOKEN_KEYS.ACCESS);
        TokenManager.removeToken(AUTH_CONSTANTS.TOKEN_KEYS.REFRESH);
        TokenManager.removeToken(AUTH_CONSTANTS.TOKEN_KEYS.API);
      } finally {
        setIsLoading(false);
        console.log('=== AuthContext: loadUser finished ===');
      }
    };

    loadUser();

    // Event-Listener für automatischen Logout
    const handleLogoutRequired = () => {
      console.log('Logout required event received');
      if (!isLoggingOut) {
        setUser(null);
        setError(null);
        TokenManager.removeAllTokens();
        
        // Redirect zur Signin-Seite
        window.history.replaceState(null, '', AUTH_CONSTANTS.ROUTES.SIGNIN);
        window.location.reload();
      }
    };

    // Storage change listener für Tab-Synchronisation
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === AUTH_CONSTANTS.TOKEN_KEYS.ACCESS || e.key === AUTH_CONSTANTS.TOKEN_KEYS.API) {
        if (!e.newValue) {
          console.log('Token removed in another tab');
          setUser(null);
          setError(null);
        }
      }
    };

    window.addEventListener(AUTH_CONSTANTS.EVENTS.LOGOUT_REQUIRED, handleLogoutRequired);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener(AUTH_CONSTANTS.EVENTS.LOGOUT_REQUIRED, handleLogoutRequired);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const login = async (credentials: { username: string; password: string }): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      useAuthStore.getState().setLoggingOut(false); // Reset logout flag
      
      console.log('🧹 Login: Clearing all old session data...');
      
      // SCHRITT 1: Alle alten Tokens löschen
      TokenManager.removeAllTokens();
      
      // SCHRITT 2: IndexedDB komplett löschen
      try {
        const databases = await window.indexedDB.databases();
        for (const db of databases) {
          if (db.name) {
            console.log(`Deleting IndexedDB: ${db.name}`);
            window.indexedDB.deleteDatabase(db.name);
          }
        }
      } catch (e) {
        console.warn('Could not clear IndexedDB:', e);
      }
      
      // SCHRITT 3: SessionStorage löschen
      sessionStorage.clear();
      
      console.log('✅ Old session data cleared, performing login...');
      
      await authService.login(credentials);
      const userData = await authService.getCurrentUser();
      setUser(userData);
      
      console.log('✅ Login successful, reloading page...');
      
      // SCHRITT 4: Hard Reload erzwingen (Safari Cache bypassen)
      setTimeout(() => {
        window.location.href = window.location.origin + '/?t=' + Date.now();
      }, 100);
      
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      const message = (err as { message?: string })?.message;
      setError(detail || message || 'Login failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithToken = async (token: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      useAuthStore.getState().setLoggingOut(false); // Reset logout flag
      
      await authService.loginWithToken(token);
      const userData = await authService.getCurrentUser();
      setUser(userData);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      const message = (err as { message?: string })?.message;
      setError(detail || message || 'Token login failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout
  const logout = (): void => {
    console.log('=== AUTHCONTEXT: Starting logout process ===');
    
    // Sofort alle Race Conditions verhindern
    if (isLoggingOut) return;
    
    useAuthStore.getState().setLoggingOut(true);
    
    // SOFORT alle States zurücksetzen (synchron!)
    console.log('Resetting all auth states...');
    setUser(null);
    setError(null);
    setIsLoading(false);
    
    // SCHRITT 1: Alle Tokens löschen
    console.log('🧹 Logout: Clearing all tokens...');
    try {
      authService.logout();
      TokenManager.removeAllTokens();
    } catch (error) {
      console.error('ERROR during token cleanup:', error);
    }
    
    // SCHRITT 2: IndexedDB komplett löschen
    console.log('🧹 Logout: Clearing IndexedDB...');
    try {
      window.indexedDB.databases().then(databases => {
        for (const db of databases) {
          if (db.name) {
            console.log(`Deleting IndexedDB: ${db.name}`);
            window.indexedDB.deleteDatabase(db.name);
          }
        }
      }).catch(e => console.warn('Could not clear IndexedDB:', e));
    } catch (e) {
      console.warn('IndexedDB cleanup failed:', e);
    }
    
    // SCHRITT 3: SessionStorage löschen
    console.log('🧹 Logout: Clearing SessionStorage...');
    try {
      sessionStorage.clear();
    } catch (error) {
      console.error('Error clearing sessionStorage:', error);
    }
    
    // SCHRITT 4: LocalStorage komplett löschen (außer essentiellen Keys)
    console.log('🧹 Logout: Clearing LocalStorage...');
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
    
    TokenManager.debugLocalStorage();
    
    console.log('✅ All session data cleared, redirecting...');
    
    // SCHRITT 5: Hard Redirect mit Cache-Bypass
    try {
      window.location.href = AUTH_CONSTANTS.ROUTES.SIGNIN + '?logout=' + Date.now();
    } catch (error) {
      console.error('Redirect failed:', error);
      window.location.replace(AUTH_CONSTANTS.ROUTES.SIGNIN);
    }
    
    console.log('=== AUTHCONTEXT: Logout process completed ===');
  };

  const refreshUser = async (): Promise<void> => {
    if (isAuthenticated && !isLoggingOut) {
      try {
        const userData = await authService.getCurrentUser();
        setUser(userData);
      } catch (err: unknown) {
        const message = (err as { message?: string })?.message ?? 'Failed to refresh user';
        setError(message);
        setUser(null);
        TokenManager.removeToken(AUTH_CONSTANTS.TOKEN_KEYS.ACCESS);
        TokenManager.removeToken(AUTH_CONSTANTS.TOKEN_KEYS.REFRESH);
        TokenManager.removeToken(AUTH_CONSTANTS.TOKEN_KEYS.API);
      }
    }
  };

  const value: LocalAuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    loginWithToken,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}